import { useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { EmptyState } from './EmptyState';
import { LoadingSpinner } from './LoadingSpinner';
import { Pagination } from './Pagination';

export interface Column<T> {
  key: string;
  header: string;
  render: (item: T) => ReactNode;
  sortable?: boolean;
  className?: string;
  /** Hide this column on screens smaller than md (768px) */
  hideOnMobile?: boolean;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  isLoading?: boolean;
  keyExtractor: (item: T) => string;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  selectable?: boolean;
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>) => void;
  sort?: string;
  sortDirection?: 'asc' | 'desc';
  onSortChange?: (key: string) => void;
  // Pagination
  page?: number;
  totalPages?: number;
  total?: number;
  limit?: number;
  onPageChange?: (page: number) => void;
}

export function DataTable<T>({
  columns,
  data,
  isLoading,
  keyExtractor,
  emptyTitle = 'No data',
  emptyDescription,
  emptyAction,
  selectable,
  selectedIds,
  onSelectionChange,
  sort,
  sortDirection,
  onSortChange,
  page,
  totalPages,
  total,
  limit,
  onPageChange,
}: DataTableProps<T>) {
  const [_selectedIds, _setSelectedIds] = useState<Set<string>>(new Set());
  const selected = selectedIds ?? _selectedIds;
  const setSelected = onSelectionChange ?? _setSelectedIds;

  const allSelected = data.length > 0 && data.every((item) => selected.has(keyExtractor(item)));

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(data.map(keyExtractor)));
    }
  };

  const toggleOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelected(next);
  };

  if (isLoading) {
    return <LoadingSpinner fullPage />;
  }

  if (data.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />;
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-default bg-bg-tertiary/50">
              {selectable && (
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className="rounded accent-accent"
                  />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={clsx(
                    'px-2 py-2 md:px-4 md:py-3 text-start text-xs font-semibold text-text-secondary',
                    col.sortable && 'cursor-pointer select-none hover:text-text-primary',
                    col.hideOnMobile && 'hidden md:table-cell',
                    col.className,
                  )}
                  onClick={col.sortable ? () => onSortChange?.(col.key) : undefined}
                >
                  <div className="flex items-center gap-1">
                    {col.header}
                    {col.sortable && (
                      <span className="text-text-muted">
                        {sort === col.key ? (
                          sortDirection === 'asc' ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : (
                            <ArrowDown className="h-3 w-3" />
                          )
                        ) : (
                          <ArrowUpDown className="h-3 w-3" />
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((item) => {
              const id = keyExtractor(item);
              return (
                <tr
                  key={id}
                  className="border-b border-border-default/40 transition-colors hover:bg-bg-hover/60"
                >
                  {selectable && (
                    <td className="w-10 px-4 py-3.5">
                      <input
                        type="checkbox"
                        checked={selected.has(id)}
                        onChange={() => toggleOne(id)}
                        className="rounded accent-accent"
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td key={col.key} className={clsx('px-2 py-2.5 md:px-4 md:py-3.5 text-sm', col.hideOnMobile && 'hidden md:table-cell', col.className)}>
                      {col.render(item)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {page && totalPages && total && limit && onPageChange && (
        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}
